import tippy from 'tippy.js';
import { takeUntil } from 'rxjs';
import { PointData } from 'pixi.js';
import { ChangeDetectorRef, Component, signal } from '@angular/core';
import { WatchAwareComponent } from '../watch-aware.component';
import { IAccountSettings, IUserAwareness } from 'src/sync/_api';
import { Settings } from 'src/graphics/settings';
import { Account } from 'src/sync/account';
import { Room } from 'src/sync/room';
import { Mouse, MouseType } from 'src/graphics/mouse';
import { Store } from 'src/sync/store';
import { View } from 'src/graphics/view';
import { ConfirmationDialog } from '../confirmation-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { UsersDialogComponent } from '../users/users-dialog.component';

@Component({
    selector: '[appFooter]',
    templateUrl: './footer.component.html',
    styleUrls: ['./footer.component.scss'],
    standalone: false
})
export class FooterComponent extends WatchAwareComponent {
    public zoom = View.viewport.scale;
    public position: PointData = { x: 0, y: 0 };
    public settings: IAccountSettings = {} as any;
    public connected = true;
    public fullAccess = false;
    public isLiberated = signal(false);
    public divisorcolor = '';
    public mousetypeHTML = '';
    public mousetypecolor = '';


    constructor(public dialogs: MatDialog, override changeDetectorRef: ChangeDetectorRef) {
        super(changeDetectorRef);
    }

    public ngAfterViewInit() {
        Store.mouse.pipe(takeUntil(this.destroyed)).subscribe((mouse) => {
            const shouldSnapAdjust = (Mouse.snapping == false || Mouse.dragTarget != null)

            this.position = {
                x: View.coerce(mouse.point.x, shouldSnapAdjust),
                y: -View.coerce(mouse.point.y, shouldSnapAdjust),
            };
            this.changeDetectorRef.detectChanges();
        });

        this.setupUserList();
        this.track(Account.settings.watch((x) => this.updateSettings(x)));
    }

    public openUsers() {
        UsersDialogComponent.open(this.dialogs);
    }

    public updateSettings(settings: IAccountSettings) {
        if (settings.name != this.settings.name || settings.color != this.settings.color) {
            Room.awareness.setLocalStateField('user', { name: settings.name, color: settings.color });
        }
        this.settings = settings;
        const colors = ['', '#002277', '#003388', '#004499', '#0055AA', '#0066BB', '#0077CC', '', '#0088DD'];
        this.divisorcolor = colors[this.settings.alignment];
        this.mousetypeHTML = this.getMousetypeHTML(this.settings.mousetype);
    }

    public getMousetypeHTML(type: MouseType) {
        switch (type) {
            case 0: return '<u>1</u> Free';
            case 1: return '<u>2</u> Box';
            case 2: return '<u>3</u> Tri';
            case 3: return '<u>4</u> Rect';
            case 4: return '<u>5</u> Pent';
            case 5: return '<u>6</u> Hex';
            case 6: return '<u>7</u> Sept';
            case 7: return '<u>8</u> Oct';
            case 8: return '<u>9</u> Non';
            case 9: return '<u>0</u> Circle';
            case 10: return '<u>P</u> Point';
            case 11: return '<u>D</u> Door';
            case 12: return '<u>R</u> Stair';
            case 13: return '<u>W</u> Wall';
            case 14: return '<u>B</u> Brush';
            case 15: return '<u>H</u> Hull';
        }

        return 'Error';
    }

    public setupUserList() {
        const tippyUsers = tippy('#users', {
            content: '',
            allowHTML: true,
            animation: 'scale',
            arrow: false
        });

        Store.connection.pipe(takeUntil(this.destroyed)).subscribe((connected) => this.connected = connected);
        Store.fullAccess.pipe(takeUntil(this.destroyed)).subscribe((fullAccess) => this.fullAccess = fullAccess);
        Store.isLiberated.pipe(takeUntil(this.destroyed)).subscribe((isLiberated) => this.isLiberated.set(isLiberated));
        Room.awareness.on('change', () => {
            const users = Room.awareness.getStateFields<IUserAwareness>('user').filter(x => x != null);
            const userHtml = users.reduce((output, user) => {
                return output += `<div class="user" style="color:${user.color};">• ${user.name}</div>`;
            }, '');
            tippyUsers.forEach(x => x.setContent(userHtml));
        });
    }

    public toggleSnap() {
        Account.settings.modify(x => x.snapping = !Mouse.snapping);
    }

    public toggleErase() {
        Mouse.toggleErasing();
    }

    public cycleMousetype(cycle: number = 1) {
        const enumLength = Object.keys(MouseType).length / 2;
        let targetType = Mouse.type += cycle;

        if (targetType == enumLength) targetType = 0;
        if (targetType == -1) targetType = enumLength - 1;
        Mouse.setMouseType(targetType);
    }

    public cycleAlignment(cycle: number = 1) {
        Mouse.cycleAlignment(cycle);
    }

    public setZoom(direction?: 'in' | 'out') {
        if (direction) {
            Mouse.zoom(direction);
        } else {
            const position = View.viewport.center.clone();
            View.viewport.setZoom(1, true);
            View.viewport.moveCenter(position.x, position.y);
            View.redraw();
        }
    }

    public moveCenter() {
        const center = Settings.worldSize / 2;
        View.viewport.moveCenter(center, center);
        this.position = { x: 0, y: 0 };
    }

    public toggleFreedom(target: boolean) {
        if (Store.isReferee.value == true) {
            ConfirmationDialog.open(this.dialogs,
                target ? 'Grant Freedom' : 'Lockdown Users',
                target ? 'Allow user to edit all layers and maps.' : 'Prevents users from editing layers or maps.',
                () => Room.settings.modify(x => x.freedom = target)
            )
        }
    }

    get users(): IUserAwareness[] {
        return Room.awareness.getStateFields<IUserAwareness>('user').filter(x => x != null);
    }
}
